import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { CurrencyService, CurrencyResponseDTO } from '../../../services/currency.service';
import { ExchangeRateService } from '../../../services/exchange-rate.service';


@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.component.html',
  styleUrl: './edit-project.component.scss'
})
export class EditProjectComponent implements OnInit, OnChanges {


  @Input() projectId!: number;
  @Output() retrieveData = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  projectForm!: FormGroup;
  projectFormArray!: FormArray;
  currentStep = 1;
  totalSteps = 3;
  categories: any[] = [];
  subcategories: any = {};
  selectedTabInStep3: any = 'Ingresos';
  showPreview: boolean = false;

  // Propiedades para monedas internacionales
  internationalCurrencies: CurrencyResponseDTO[] = [];
  selectedCurrency: CurrencyResponseDTO | null = null;
  currentExchangeRate: number | null = null;
  lastRateUpdate: Date | null = null;

  projectTypes = [
    { value: 1, label: 'Diploma', backendValue: 'DIPLOMA' },
    { value: 2, label: 'Seminario', backendValue: 'SEMINAR' },
    { value: 3, label: 'Especialización', backendValue: 'SPECIALIZATION' },
    { value: 4, label: 'Consultoria', backendValue: 'CONSULTING' },
    { value: 5, label: 'Pre-grado', backendValue: 'PREGRADUATE' },
    { value: 6, label: 'Postgrado', backendValue: 'POSTGRADUATE' },
    { value: 7, label: 'Investigación', backendValue: 'INVESTIGATION' },
    { value: 8, label: 'Bienestar', backendValue: 'WELFARE' },
    { value: 9, label: 'Otro', backendValue: 'OTHER' },
  ];

  projectStatus = [
    { value: 1, label: 'Creado', backendValue: 'CREATED' },
    { value: 2, label: 'Cerrado', backendValue: 'CLOSED' },
    { value: 3, label: 'Precierre', backendValue: 'PRECLOSED' },
    { value: 4, label: 'Inactivo', backendValue: 'INACTIVE' },
    { value: 5, label: 'Cancelado', backendValue: 'CANCELED' },
  ];

  months = [
    { value: 'ENE', label: 'Enero' },
    { value: 'FEB', label: 'Febrero' },
    { value: 'MAR', label: 'Marzo' },
    { value: 'ABR', label: 'Abril' },
    { value: 'MAY', label: 'Mayo' },
    { value: 'JUN', label: 'Junio' },
    { value: 'JUL', label: 'Julio' },
    { value: 'AGO', label: 'Agosto' },
    { value: 'SEP', label: 'Septiembre' },
    { value: 'OCT', label: 'Octubre' },
    { value: 'NOV', label: 'Noviembre' },
    { value: 'DIC', label: 'Diciembre' },
  ]

  // Mapeo para convertir strings del backend a números del frontend
  private statusStringToNumber: { [key: string]: number } = {
    'CREATED': 1,
    'CLOSED': 2,
    'PRECLOSED': 3,
    'INACTIVE': 4,
    'CANCELED': 5
  };

  private typeStringToNumber: { [key: string]: number } = {
    'DIPLOMA': 1,
    'SEMINAR': 2,
    'SPECIALIZATION': 3,
    'CONSULTING': 4,
    'PREGRADUATE': 5,
    'POSTGRADUATE': 6,
    'INVESTIGATION': 7,
    'WELFARE': 8,
    'OTHER': 9
  };
  constructor(
    private fb: FormBuilder,
    private _projectService: ProjectService,
    private toastService: ToastService,
    private _loadingService: LoadingService,
    private _currencyService: CurrencyService,
    private _exchangeRateService: ExchangeRateService
  ) {
    this.projectForm = this.fb.group({
      projectId: ['', Validators.required],
      name: ['', Validators.required],
      inscriptionAmountCOP: ['', Validators.required],
      description: [''],
      period: ['', Validators.required],
      status: [1, Validators.required], // CREATED por defecto
      date: [''],
      dateClosed: [''],
      typeProject: [1, Validators.required], // DIPLOMA por defecto
      duration: ['', [Validators.required, Validators.min(1)]],
      noParticipants: [0],
      projectedParticipants: ['', [Validators.required, Validators.min(0)]],
      observations: [''],
      projectedRevenue: ['', [Validators.required, Validators.min(0)]],
      projectedSpend: ['', [Validators.required, Validators.min(0)]],
      totalIncome: [0],
      totalExpenses: [0],

      // Campos para inscripciones internacionales
      currencyId: [1], // COP por defecto
      inscriptionAmountUSD: ['', [Validators.required, Validators.min(0.01)]],
      currencyCode: ['']
    });
  }



  ngOnInit(): void {
    this.setupConditionalValidations();
    this.loadInternationalCurrencies();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.getProjectById(this.projectId);
    }
  }


  getProjectById(id: number) {
    this._projectService.getById(id).subscribe({
      next: (response) => {

        const formatDate = (date: string | null) => date ? new Date(date).toISOString().split('T')[0] : '';

        this.projectForm.patchValue({
          projectId: response.data.projectId,
          name: response.data.name,
          inscriptionAmountCOP: response.data.inscriptionAmountCOP,
          description: response.data.description,
          period: response.data.period,
          status: this.statusStringToNumber[response.data.status] || 1,
          date: formatDate(response.data.date),
          dateClosed: formatDate(response.data.dateClosed),
          typeProject: this.typeStringToNumber[response.data.typeProject] || 1,
          duration: response.data.duration,
          noParticipants: response.data.noParticipants,
          projectedParticipants: response.data.projectedParticipants,
          observations: response.data.observations,
          projectedRevenue: response.data.projectedRevenue,
          projectedSpend: response.data.projectedSpend,
          totalIncome: response.data.totalIncome,
          totalExpenses: response.data.totalExpenses,

          // Campos para inscripciones internacionales
          currencyId: Number(response.data.currencyId) || 1,
          inscriptionAmountUSD: response.data.inscriptionAmountUSD || null,
          currencyCode: response.data.currency?.code || ''
        });

        // Si es internacional, cargar información de moneda
        if (response.data.currencyId && response.data.currencyId > 1 && response.data.currency) {
          this.selectedCurrency = response.data.currency;
          this.currentExchangeRate = response.data.currentExchangeRate || null;
          this.lastRateUpdate = response.data.lastExchangeRateUpdate ? new Date(response.data.lastExchangeRateUpdate) : null;
        }

        // Asegurar que las validaciones se apliquen después de cargar los datos
        setTimeout(() => {
          const currencyId = this.projectForm.get('currencyId')?.value;
          const isInternational = this.isInternationalProject(currencyId);
          this.updateInternationalValidations(isInternational);

          // Debug para verificar valores
          console.log('Datos cargados en edición:', {
            status: this.projectForm.get('status')?.value,
            typeProject: this.projectForm.get('typeProject')?.value,
            currencyId: this.projectForm.get('currencyId')?.value,
            rawData: {
              status: response.data.status,
              typeProject: response.data.typeProject,
              currencyId: response.data.currencyId
            }
          });

          // Forzar detección de cambios
          this.projectForm.updateValueAndValidity();
        }, 100);
      },
      error: (error) => {
        console.error(error)
      }
    })
  }

  setupConditionalValidations(): void {
    this.projectForm.get('date')?.valueChanges.subscribe((startDate) => {
      const dateClosedControl = this.projectForm.get('dateClosed');
      if (startDate) {
        dateClosedControl?.setValidators([Validators.required]);
      } else {
        dateClosedControl?.clearValidators();
      }
      dateClosedControl?.updateValueAndValidity();
    });

    // Validaciones condicionales para proyectos internacionales
    this.projectForm.get('currencyId')?.valueChanges.subscribe((currencyId) => {
      const isInternational = this.isInternationalProject(currencyId);
      this.updateInternationalValidations(isInternational);
    });
  }

  // Método helper para determinar si un proyecto es internacional
  isInternationalProject(currencyId: any): boolean {
    return currencyId && currencyId > 1; // Internacional si tiene una moneda diferente a COP (ID 1)
  }

  updateInternationalValidations(isInternational: boolean): void {
    const currencyIdControl = this.projectForm.get('currencyId');
    const inscriptionAmountUSDControl = this.projectForm.get('inscriptionAmountUSD');
    const inscriptionAmountCOPControl = this.projectForm.get('inscriptionAmountCOP');

    // Ambos campos son obligatorios
    inscriptionAmountCOPControl?.setValidators([Validators.required, Validators.min(0.01)]);
    inscriptionAmountUSDControl?.setValidators([Validators.required, Validators.min(0.01)]);
    currencyIdControl?.clearValidators(); // CurrencyId es opcional, COP por defecto

    currencyIdControl?.updateValueAndValidity();
    inscriptionAmountUSDControl?.updateValueAndValidity();
    inscriptionAmountCOPControl?.updateValueAndValidity();
  }


  isCurrentStepValid(): boolean | undefined {
    switch (this.currentStep) {
      case 1:
        const baseValidation = this.projectForm.get('name')?.valid &&
          this.projectForm.get('projectId')?.valid &&
          this.projectForm.get('period')?.valid &&
          this.projectForm.get('status')?.valid &&
          this.projectForm.get('date')?.valid &&
          this.projectForm.get('dateClosed')?.valid &&
          this.projectForm.get('typeProject')?.valid &&
          this.projectForm.get('duration')?.valid &&
          this.projectForm.get('projectedParticipants')?.valid &&
          true; // No hay validación específica para isInternational

        // Validamos tanto COP como USD como campos obligatorios
        return baseValidation &&
          this.projectForm.get('inscriptionAmountCOP')?.valid &&
          this.projectForm.get('inscriptionAmountUSD')?.valid;
      case 2:
        return this.projectForm.get('projectedRevenue')?.valid &&
          this.projectForm.get('projectedSpend')?.valid;
      default:
        return true; // El paso final no requiere validación adicional
    }
  }

  nextStep(): void {
    if (this.isCurrentStepValid()) {
      this.currentStep++;
    } else {
      this.projectForm.markAllAsTouched(); // Muestra los errores en los campos inválidos
    }
  }

  prevStep() {
    this.currentStep--;
  }

  onSubmit() {
    this._loadingService.show();
    console.log("before", this.projectForm.value)
    const newProject = {
      ...this.projectForm.value,
      status: Number(this.projectForm.value.status),
      typeProject: Number(this.projectForm.value.typeProject),

      currencyId: this.projectForm.value.currencyId ? Number(this.projectForm.value.currencyId) : null
    };
    this._projectService.update(this.projectId, newProject).subscribe({
      next: (response: any) => {
        this.handleSuccess('Proyecto actualizado correctamente.');
      },
      error: (err) => {
        this.handleError('No se pudo actualizar el proyecto.');
      }
    })
    console.log("after", newProject)
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.closeModal.emit();
    setTimeout(() => window.location.reload(), 500)

  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  // Métodos para manejo de monedas internacionales
  loadInternationalCurrencies(): void {
    this._currencyService.getInternationalCurrencies().subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.internationalCurrencies = response.data;
        }
      },
      error: (error) => {
        console.error('Error cargando monedas internacionales:', error);
      }
    });
  }

  onInscriptionTypeChange(): void {
    const currencyId = this.projectForm.get('currencyId')?.value;
    const isInternational = this.isInternationalProject(currencyId);
    if (!isInternational) {
      // Limpiar campos internacionales
      this.projectForm.patchValue({
        currencyId: null,
        inscriptionAmountUSD: null,
        currencyCode: ''
      });
      this.resetCurrencyData();
    }
  }

  onCurrencyChange(): void {
    const currencyId = this.projectForm.get('currencyId')?.value;
    if (currencyId) {
      this.selectedCurrency = this.internationalCurrencies.find(c => c.id === Number(currencyId)) || null;
      if (this.selectedCurrency) {
        this.projectForm.patchValue({ currencyCode: this.selectedCurrency.code });
        this.loadCurrentExchangeRate();
      }
    } else {
      this.resetCurrencyData();
    }
  }

  loadCurrentExchangeRate(): void {
    if (this.selectedCurrency) {
      this._exchangeRateService.getLatestRate(this.selectedCurrency.code, 'COP').subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.currentExchangeRate = response.rate;
            this.lastRateUpdate = new Date(response.timestamp);
            this.calculateCOPEquivalent();
          }
        },
        error: (error) => {
          console.error('Error cargando tasa de cambio:', error);
          this.toastService.showToast('warning', 'No se pudo cargar la tasa de cambio actual');
          this.currentExchangeRate = 1; // Fallback
        }
      });
    }
  }

  calculateCOPEquivalent(): void {
    const originalAmount = this.projectForm.get('inscriptionAmountUSD')?.value;
    if (originalAmount && this.currentExchangeRate) {
      const copAmount = originalAmount * this.currentExchangeRate;
      this.projectForm.patchValue({ inscriptionAmountCOP: copAmount });
    }
  }

  resetCurrencyData(): void {
    this.selectedCurrency = null;
    this.currentExchangeRate = null;
    this.lastRateUpdate = null;
  }

  getSelectedCurrencyCode(): string {
    return this.selectedCurrency?.code || 'USD';
  }

  formatExchangeRate(rate: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(rate);
  }
}
